import { common, grey, red, lightBlue } from "@mui/material/colors"

function isDarkMode(theme) {
  return theme.palette.mode === "dark"
}

function buildAlertStandardStyle(palette, type) {
  return {
    errorStandardBg: palette.error[type] ?? palette.error.main,
    successStandardBg: palette.success[type] ?? palette.success.main,
    warningStandardBg: palette.warning[type] ?? palette.warning.main,
    warningColor: palette.text.primary,
    infoColor: palette.text.primary,
  }
}

function getDarkElevationStyles(theme, level = "base") {
  if (!isDarkMode(theme)) {
    return {}
  }

  const elevationByLevel = {
    base: {
      backgroundColor: "var(--app-op-surfaces-elevated)",
      border: "1px solid var(--app-overlay-border)",
      boxShadow: "0 8px 20px rgba(0, 0, 0, 0.28), 0 1px 2px rgba(0, 0, 0, 0.5)",
    },
    floating: {
      backgroundColor: "var(--app-op-surfaces-elevated)",
      border: "1px solid var(--app-overlay-border)",
      boxShadow: "0 12px 28px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.55)",
    },
  }

  return elevationByLevel[level] ?? elevationByLevel.base
}

// Paper is the root for many MUI components; only "plain" Paper should get the global dark
// elevated surface (border + shadow). Composites have their own theme slots or distinct visuals.
const DARK_MODE_ELEVATED_PAPER_SELECTOR =
  "&:not(.MuiAccordion-root):not(.MuiAlert-root):not(.MuiAppBar-root):not(.MuiAutocomplete-paper):not(.MuiCard-root):not(.MuiDialog-paper):not(.MuiDrawer-paper):not(.MuiMenu-paper):not(.MuiMobileStepper-root):not(.MuiPopover-paper):not(.MuiSnackbarContent-root)"

const lightPaletteBase = {
  mode: "light",
  common: { background: common.white, onBackground: common.black },
  primary: {
    main: "#03a9f4",
    light: "rgb(230, 246, 254)",
    contrastText: common.white,
  },
  success: { main: "#4caf50", light: "#f0f8e5" },
  highlight: { bg: "#ffc" },
  secondary: { main: "#607d8b" },
  background: { default: "#ffffff", paper: "#ffffff" },
  text: {
    primary: "#333333",
    secondary: "rgba(0, 0, 0, 0.7)",
    disabled: "rgba(0, 0, 0, 0.38)",
  },
  divider: "rgba(0, 0, 0, 0.12)",
  warning: { main: "#ed6c02", light: "#fff4e5" },
  error: { main: "#d32f2f", light: red[400], dark: red[800] },
  info: { main: lightBlue[700], light: lightBlue[500], dark: lightBlue[900] },
  grey,
  action: { hover: "#f5f5f5", selected: "rgba(0, 0, 0, 0.08)" },
  chart: {
    total: "#00abe8",
    factor: "#9067a7",
    security: "#7293cb",
    alpha: "#627b8c",
    trading: "#da7c30",
    riskFree: "#2e8b57",
    series: [
      "#da7c30", "#396ab1", "#3e9651", "#cc2529", "#535154",
      "#6b4c9a", "#922428", "#948b3d", "#9b3d70", "#237994",
      "#FFCF0D", "#19FFE7", "#E51BF5", "#74F50F", "#015175",
    ],
  },
}

const lightPalette = {
  ...lightPaletteBase,
  Alert: buildAlertStandardStyle(lightPaletteBase, "light"),
}

const darkPaletteBase = {
  mode: "dark",
  common: { background: common.black, onBackground: common.white },
  primary: {
    main: "#0277bd",
    light: "rgba(59, 130, 246, 0.12)",
    contrastText: common.white,
  },
  success: {
    main: "#66bb6a",
    light: "rgba(102, 187, 106, 0.2)",
    dark: "#4a8f4e",
  },
  warning: {
    main: "#ffb74d",
    light: "rgba(255, 183, 77, 0.2)",
    dark: "#e65100",
  },
  highlight: { bg: "rgba(255, 235, 59, 0.25)" },
  secondary: { main: "#94a3b8" },
  background: { default: "#121212", paper: "#181818" },
  text: {
    primary: "#f0f0f0",
    secondary: "rgba(255, 255, 255, 0.72)",
    disabled: "rgba(255, 255, 255, 0.38)",
  },
  divider: "rgba(255, 255, 255, 0.09)",
  error: { main: "#e07a95", light: "#f0a5b8", dark: "#c76570" },
  info: { main: "#14b8a6", light: "#5eead4", dark: "#0d9488" },
  grey,
  action: { hover: "#2a2a2a", selected: "rgba(255, 255, 255, 0.08)" },
  chart: {
    total: "#3b82f6",
    factor: "#a78bfa",
    security: "#94a3b8",
    alpha: "#64748b",
    trading: "#f59e0b",
    riskFree: "#2dd4bf",
    series: [
      "#3b82f6", "#a78bfa", "#64748b", "#2dd4bf", "#e07a95",
      "#94a3b8", "#c084fc", "#5eead4", "#f0a5b8", "#67e8f9",
      "#fbbf24", "#a5f3fc", "#c4b5fd", "#6ee7b7", "#f9a8d4",
    ],
  },
}

const darkPalette = {
  ...darkPaletteBase,
  Alert: buildAlertStandardStyle(darkPaletteBase, "dark"),
}

// Palette values are exposed as CSS variables (--mui-palette-*) when cssVariables: true.
// Base light/dark provide most coloring; alternates only override Figma-specified keys (primary,
// secondary, background, text, divider, action hover/selected, chart, feedback) and inherit the rest.
// Keep in sync with styles/_theme-palette.scss for non-MUI overrides.
export default {
  cssVariables: true,
  colorSchemeSelector: "data-mui-color-scheme",
  colorSchemes: {
    light: { palette: lightPalette },
    dark: { palette: darkPalette },
  },
  components: {
    MuiButton: {
      color: "inherit",
      styleOverrides: {
        root: ({ theme }) => ({
          "&:not(.MuiButton-contained)": {
            color: "inherit",
          },
          "&.MuiButton-outlined:not(.MuiButton-colorPrimary):hover, &.MuiButton-text:not(.MuiButton-colorPrimary):hover":
            {
              backgroundColor: isDarkMode(theme)
                ? theme.palette.action?.hover
                : "rgba(0, 0, 0, 0.06)",
            },
        }),
      },
    },
    MuiButtonBase: {
      disableRipple: true,
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--app-modal-backdrop-surface-bg)",
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          paddingTop: 12,
          paddingBottom: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...getDarkElevationStyles(theme, "base"),
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) =>
          isDarkMode(theme)
            ? {
              // Scope to default filled chips only; keep semantic color chips
              // (success/warning/error/primary/etc.) driven by MUI palette.
              "&&.MuiChip-filled.MuiChip-colorDefault": {
                backgroundColor: "rgba(244, 248, 255, 0.12)",
              },
            }
            : {},
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          "&:not(.Mui-error)": {
            color: "var(--mui-palette-text-secondary)",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          "& input": {
            color: "var(--mui-palette-text-primary)",
          },
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--mui-palette-background-paper)",
          color: "var(--mui-palette-text-primary)",
          ".MuiPopover-root &": { backgroundColor: "transparent" },
          ".MuiMenu-root &": { backgroundColor: "transparent" },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: ({ theme }) =>
          isDarkMode(theme)
            ? {
              "&.MuiLink-button": {
                color: "var(--app-link-button-on-muted-bg)",
              },
            }
            : {},
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => ({
          ...getDarkElevationStyles(theme, "floating"),
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme, ownerState }) => {
          if (!isDarkMode(theme)) {
            return {}
          }

          const noElevationGradient = { backgroundImage: "none" }

          if (ownerState?.elevation === 0) {
            return noElevationGradient
          }

          return {
            ...noElevationGradient,
            [DARK_MODE_ELEVATED_PAPER_SELECTOR]: getDarkElevationStyles(theme, "base"),
          }
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: ({ theme }) => ({
          ...getDarkElevationStyles(theme, "floating"),
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          ...getDarkElevationStyles(theme, "floating"),
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: "var(--mui-palette-text-primary)",
          minWidth: 160,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) =>
          isDarkMode(theme)
            ? {
              backgroundColor: "var(--app-op-surfaces-muted)",
              border: "1px solid var(--app-overlay-border)",
              boxShadow: "0 8px 18px rgba(0, 0, 0, 0.35)",
            }
            : {},
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          margin: 0,
          backgroundColor: "var(--app-bg-body)",
        },
      },
    },
  },
  typography: {
    fontSize: 16,
    fontFamily: ['"Open Sans"', '"Helvetica"', '"Arial"', "sans-serif"].join(","),
    fontWeightMedium: 600,
    body1: {
      fontFamily: ['"Open Sans"', '"Helvetica"', '"Arial"', "sans-serif"].join(","),
    },
  },
}
